import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';
import { 
  Sparkles, 
  Building2, 
  MapPin, 
  Target, 
  Zap,
  TrendingUp,
  Loader2,
  CheckCircle,
  Info,
  FileText,
  Lightbulb,
  Search,
  AlertCircle,
  Globe,
  Tag,
  Radio
} from 'lucide-react';

const OPPORTUNITY_TYPES = [
  { value: 'new_project', label: 'New Project' },
  { value: 'renovation', label: 'Renovation' },
  { value: 'expansion', label: 'Expansion' },
  { value: 'rebrand', label: 'Rebrand' },
  { value: 'new_opening', label: 'New Opening' },
  { value: 'acquisition', label: 'Acquisition' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'other', label: 'Other' },
];

const STAGES = [
  { value: 'prospecting', label: 'Prospecting' },
  { value: 'strategic_discovery', label: 'Strategic Discovery' },
  { value: 'vision_deck_presented', label: 'Vision Deck Presented' },
  { value: 'executive_deck_presented', label: 'Executive Deck Presented' },
  { value: 'refinement', label: 'Refinement' },
  { value: 'production_alignment', label: 'Production Alignment' },
];

// Property type labels for working titles
const PROPERTY_TYPE_LABELS = {
  'new_hotel': 'Hotel Development',
  'new_opening': 'Hospitality Project',
  'renovation': 'Renovation Project',
  'rebrand': 'Rebrand Project',
  'expansion': 'Expansion Project',
  'territory_expansion': 'Market Expansion',
  'acquisition': 'Acquisition Target',
  'branch_opening': 'New Location',
  'hiring_surge': 'Growing Company',
  'sales_hiring': 'Expanding Organization',
  'market_growth': 'Market Opportunity',
  'new_gm': 'Leadership Change',
  'development_permits': 'Development Project',
  'construction': 'Construction Project',
  'franchise_activity': 'Franchise Opportunity',
};

// Determine identity confidence level
const getIdentityConfidence = (signal) => {
  if (!signal) return { level: 'unknown', label: 'Unknown', color: 'gray' };
  
  const hasCompanyName = signal.company_name && signal.company_name.trim() && signal.company_name.toLowerCase() !== 'none';
  const hasTitle = signal.title && signal.title.trim() && signal.title.toLowerCase() !== 'n/a';
  const hasWebsite = signal.website || signal.company_website;
  const hasLinkedIn = signal.linkedin_url;
  
  if (hasCompanyName && (hasWebsite || hasLinkedIn)) {
    return { level: 'confirmed', label: 'Confirmed', color: 'emerald', icon: '🟢' };
  } else if (hasCompanyName || (hasTitle && !hasTitle.toLowerCase().includes('unnamed'))) {
    return { level: 'likely', label: 'Likely', color: 'amber', icon: '🟡' };
  } else {
    return { level: 'early', label: 'Early Signal', color: 'red', icon: '🔴' };
  }
};

// Generate a working title when name is unknown
const generateWorkingTitle = (signal) => {
  if (!signal) return 'Unnamed Opportunity';
  
  const propertyType = PROPERTY_TYPE_LABELS[signal.signal_type] || 'Hospitality Project';
  const location = signal.location || {};
  const city = location.city || '';
  const area = city || location.province_state || location.country || 'Unknown Location';
  
  return `Unnamed ${propertyType} – ${area}`;
};

// Check if the name is a generated working title
const isGeneratedTitle = (name) => {
  return name && name.startsWith('Unnamed ');
};

// Generate improved AI summary that avoids vague phrasing
const generateAISummary = (signal, companyName, identityConfidence) => {
  if (!signal) return '';
  
  const signalType = signal.signal_type || 'opportunity';
  const location = signal.location;
  const locationStr = location ? `${location.city || ''}${location.city && location.province_state ? ', ' : ''}${location.province_state || ''}` : '';
  const strength = signal.signal_strength || 'medium';
  const confidence = signal.signal_confidence || 'medium';
  
  // Use proper entity reference based on identity confidence
  const isEarlySignal = identityConfidence.level === 'early';
  const propertyType = PROPERTY_TYPE_LABELS[signalType] || 'hospitality development';
  
  let summary = '';
  
  // Signal type specific summaries - avoid vague "this company" language
  if (isEarlySignal) {
    // Early signal - use property-centric language
    if (signalType === 'new_hotel' || signalType === 'new_opening') {
      summary = `A new ${propertyType.toLowerCase()} is underway${locationStr ? ` in ${locationStr}` : ''}. This represents a prime opportunity to establish a relationship early in the development cycle when design decisions are being made.`;
    } else if (signalType === 'renovation') {
      summary = `A renovation project has been identified${locationStr ? ` in ${locationStr}` : ''}. Renovation projects typically have defined budgets and timelines, making this an ideal time to present design solutions.`;
    } else if (signalType === 'expansion' || signalType === 'territory_expansion') {
      summary = `Market expansion activity detected${locationStr ? ` in ${locationStr}` : ''}. Companies in expansion mode often seek consistent design partners who can scale with their growth.`;
    } else if (signalType === 'acquisition') {
      summary = `An acquisition signal has been detected${locationStr ? ` in ${locationStr}` : ''}. Post-acquisition periods often trigger rebranding and renovation projects.`;
    } else if (signalType === 'development_permits' || signalType === 'construction') {
      summary = `A new ${propertyType.toLowerCase()} has been identified${locationStr ? ` in ${locationStr}` : ''}. Early engagement could position us well for upcoming design requirements.`;
    } else {
      summary = `An early-stage opportunity has been detected${locationStr ? ` in ${locationStr}` : ''}. This signal indicates potential design needs that warrant further investigation.`;
    }
  } else {
    // Known entity - can reference by name
    const entityName = companyName || 'This organization';
    if (signalType === 'new_hotel' || signalType === 'new_opening') {
      summary = `${entityName} is developing a new property${locationStr ? ` in ${locationStr}` : ''}. This represents a prime opportunity to establish a relationship early in their development cycle when design decisions are being made.`;
    } else if (signalType === 'renovation') {
      summary = `${entityName} has announced renovation plans${locationStr ? ` for their ${locationStr} location` : ''}. Renovation projects typically have defined budgets and timelines, making this an ideal time to present design solutions.`;
    } else if (signalType === 'expansion' || signalType === 'territory_expansion') {
      summary = `${entityName} is expanding their footprint${locationStr ? ` into ${locationStr}` : ''}. Companies in expansion mode often seek consistent design partners who can scale with their growth.`;
    } else if (signalType === 'acquisition') {
      summary = `${entityName} is involved in an acquisition${locationStr ? ` in ${locationStr}` : ''}. Post-acquisition periods often trigger rebranding and renovation projects.`;
    } else if (signalType === 'hiring_surge' || signalType === 'sales_hiring') {
      summary = `${entityName} is actively hiring${locationStr ? ` in ${locationStr}` : ''}, indicating growth and potential budget for property improvements and new projects.`;
    } else {
      summary = `${entityName}${locationStr ? ` in ${locationStr}` : ''} has shown signals of activity that align with potential design needs. Early engagement could position us well for upcoming projects.`;
    }
  }
  
  // Add confidence note
  if (strength === 'high' && confidence === 'high') {
    summary += ' Signal strength and confidence are both high, suggesting strong lead potential.';
  } else if (strength === 'high' || confidence === 'high') {
    summary += ' This signal shows promising indicators worth pursuing.';
  }
  
  return summary;
};

// Map signal type to opportunity type
const mapSignalToOpportunityType = (signalType) => {
  const mapping = {
    'new_hotel': 'new_opening',
    'new_opening': 'new_opening',
    'renovation': 'renovation',
    'rebrand': 'rebrand',
    'expansion': 'expansion',
    'territory_expansion': 'expansion',
    'acquisition': 'acquisition',
    'branch_opening': 'new_opening',
    'hiring_surge': 'new_project',
    'sales_hiring': 'new_project',
    'market_growth': 'expansion',
    'new_gm': 'consultation',
    'development_permits': 'new_project',
    'construction': 'new_project',
  };
  return mapping[signalType] || 'other';
};

// Get human-readable signal type label
const getSignalTypeLabel = (signalType) => {
  const labels = {
    'new_hotel': 'New Hotel',
    'new_opening': 'New Opening',
    'renovation': 'Renovation',
    'rebrand': 'Rebrand',
    'expansion': 'Expansion',
    'territory_expansion': 'Territory Expansion',
    'acquisition': 'Acquisition',
    'branch_opening': 'Branch Opening',
    'hiring_surge': 'Hiring Surge',
    'sales_hiring': 'Sales Hiring',
    'market_growth': 'Market Growth',
    'new_gm': 'New GM',
    'development_permits': 'Development Permits',
    'construction': 'Construction',
    'franchise_activity': 'Franchise Activity',
  };
  return labels[signalType] || signalType?.replace(/_/g, ' ') || 'Unknown';
};

// Get human-readable property type
const getPropertyTypeLabel = (signalType) => {
  const labels = {
    'new_hotel': 'Hotel',
    'new_opening': 'Hospitality Property',
    'renovation': 'Existing Property',
    'rebrand': 'Property Rebrand',
    'expansion': 'Commercial Property',
    'territory_expansion': 'Market Entry',
    'acquisition': 'Acquisition Target',
    'branch_opening': 'New Location',
    'hiring_surge': 'Corporate',
    'sales_hiring': 'Corporate',
    'market_growth': 'Commercial',
    'new_gm': 'Hotel/Resort',
    'development_permits': 'Development',
    'construction': 'New Build',
  };
  return labels[signalType] || 'Property';
};

export function CreateOpportunityModal({ 
  isOpen, 
  onClose, 
  signal = null, // Pre-fill data from signal
  onSuccess = null // Callback when opportunity is created
}) {
  const navigate = useNavigate();
  const { post } = useApi();
  const [loading, setLoading] = useState(false);
  const [creatingFully, setCreatingFully] = useState(false);
  const [enriching, setEnriching] = useState(false);
  
  // Form state
  const [companyName, setCompanyName] = useState('');
  const [city, setCity] = useState('');
  const [provinceState, setProvinceState] = useState('');
  const [country, setCountry] = useState('');
  const [opportunityType, setOpportunityType] = useState('new_project');
  const [stage, setStage] = useState('prospecting');
  const [estimatedValue, setEstimatedValue] = useState('');
  const [confidenceScore, setConfidenceScore] = useState('');
  const [aiSummary, setAiSummary] = useState('');
  const [notes, setNotes] = useState('');
  const [identityConfidence, setIdentityConfidence] = useState({ level: 'unknown', label: 'Unknown', color: 'gray', icon: '⚪' });
  const [isWorkingTitle, setIsWorkingTitle] = useState(false);
  
  const isPrefilledFromSignal = signal !== null;

  // Pre-fill form when signal data is provided
  useEffect(() => {
    if (signal && isOpen) {
      // Determine identity confidence
      const confidence = getIdentityConfidence(signal);
      setIdentityConfidence(confidence);
      
      // Determine company name - NEVER leave empty
      let name = '';
      const hasValidName = signal.company_name && 
                          signal.company_name.trim() && 
                          signal.company_name.toLowerCase() !== 'none' &&
                          signal.company_name.toLowerCase() !== 'null';
      const hasValidTitle = signal.title && 
                           signal.title.trim() && 
                           signal.title.toLowerCase() !== 'n/a' &&
                           signal.title.toLowerCase() !== 'null';
      
      if (hasValidName) {
        name = signal.company_name;
        setIsWorkingTitle(false);
      } else if (hasValidTitle) {
        name = signal.title;
        setIsWorkingTitle(false);
      } else {
        // Generate working title
        name = generateWorkingTitle(signal);
        setIsWorkingTitle(true);
      }
      setCompanyName(name);
      
      // Location
      const loc = signal.location || {};
      setCity(loc.city || '');
      setProvinceState(loc.province_state || loc.state || '');
      setCountry(loc.country || 'Canada');
      
      // Opportunity type based on signal type
      setOpportunityType(mapSignalToOpportunityType(signal.signal_type));
      
      // Confidence score
      const confScore = signal.signal_confidence === 'high' ? '90' : 
                        signal.signal_confidence === 'medium' ? '70' : '50';
      setConfidenceScore(confScore);
      
      // Generate AI summary with improved language
      setAiSummary(generateAISummary(signal, name, confidence));
      
      // Default stage
      setStage('prospecting');
      
      // Default estimated value based on signal type
      const defaultValues = {
        'new_hotel': '75000',
        'renovation': '50000',
        'expansion': '60000',
        'new_opening': '75000',
        'acquisition': '100000',
        'development_permits': '80000',
        'construction': '90000',
      };
      setEstimatedValue(defaultValues[signal.signal_type] || '50000');
      
      // Pre-fill notes with signal context
      setNotes(`Source: Market Signal - ${signal.signal_type?.replace(/_/g, ' ') || 'Unknown'}\n${signal.description || ''}`);
    }
  }, [signal, isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCompanyName('');
      setCity('');
      setProvinceState('');
      setCountry('');
      setOpportunityType('new_project');
      setStage('prospecting');
      setEstimatedValue('');
      setConfidenceScore('');
      setAiSummary('');
      setNotes('');
      setIdentityConfidence({ level: 'unknown', label: 'Unknown', color: 'gray', icon: '⚪' });
      setIsWorkingTitle(false);
    }
  }, [isOpen]);

  // Handle company name change - update working title status
  const handleCompanyNameChange = (value) => {
    setCompanyName(value);
    setIsWorkingTitle(isGeneratedTitle(value));
  };

  // Handle enrich identity
  const handleEnrichIdentity = async () => {
    if (!signal) return;
    
    setEnriching(true);
    try {
      // Try to enrich the signal identity using existing enrichment APIs
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lead-intelligence/resolve-and-enrich/${signal.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.company_name || data.resolved_company) {
          const newName = data.company_name || data.resolved_company;
          setCompanyName(newName);
          setIsWorkingTitle(false);
          setIdentityConfidence({ level: 'likely', label: 'Likely', color: 'amber', icon: '🟡' });
          toast.success(`Identity enriched: ${newName}`);
          
          // Update AI summary with new name
          setAiSummary(generateAISummary(signal, newName, { level: 'likely' }));
        } else {
          toast.info('No additional identity information found');
        }
      } else {
        toast.error('Could not enrich identity at this time');
      }
    } catch (error) {
      console.error('Enrich identity failed:', error);
      toast.error('Enrichment service unavailable');
    } finally {
      setEnriching(false);
    }
  };

  const handleSubmit = async (e, fullyBuilt = false) => {
    e?.preventDefault();
    
    if (!companyName.trim()) {
      toast.error('Company name is required');
      return;
    }

    setLoading(true);
    if (fullyBuilt) setCreatingFully(true);

    try {
      const opportunityData = {
        company_name: companyName.trim(),
        location: {
          city: city.trim(),
          province_state: provinceState.trim(),
          country: country.trim() || 'Canada',
        },
        opportunity_type: opportunityType,
        stage: fullyBuilt ? 'prospecting' : stage,
        estimated_value: parseFloat(estimatedValue) || 50000,
        source: isPrefilledFromSignal ? 'Market Signal' : 'Manual Entry',
        source_signal_id: signal?.id || null,
        source_signal_type: signal?.signal_type || null,
        confidence_score: parseInt(confidenceScore) || null,
        identity_confidence: identityConfidence.level,
        is_early_signal: identityConfidence.level === 'early',
        notes: fullyBuilt 
          ? `${notes}\n\n--- AI Opportunity Summary ---\n${aiSummary}`
          : notes,
      };

      const response = await post('/opportunities', opportunityData);
      
      if (response && response.id) {
        const actionVerb = identityConfidence.level === 'early' ? 'tracked' : 'created';
        toast.success(fullyBuilt 
          ? `Development ${actionVerb} successfully!` 
          : `Opportunity ${actionVerb} successfully!`
        );
        
        // If there's a signal, mark it as converted
        if (signal?.id) {
          try {
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market-intelligence/signals/${signal.id}/link-opportunity`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ opportunity_id: response.id })
            });
          } catch (e) {
            console.log('Could not link signal to opportunity');
          }
        }
        
        onClose();
        
        if (onSuccess) {
          onSuccess(response);
        } else if (fullyBuilt) {
          // Navigate to the new opportunity
          navigate(`/opportunities/${response.id}`);
        }
      }
    } catch (error) {
      console.error('Failed to create opportunity:', error);
      toast.error('Failed to create opportunity');
    } finally {
      setLoading(false);
      setCreatingFully(false);
    }
  };

  if (!isOpen) return null;

  // Dynamic CTA text based on identity confidence
  const primaryCtaText = identityConfidence.level === 'early' ? 'Track This Development' : 'Create Opportunity';
  const secondaryCtaText = identityConfidence.level === 'early' ? 'Track & Build Profile' : 'Create Fully Built';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#C9A227]/10">
              <Target className="w-5 h-5 text-[#C9A227]" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-xl font-semibold">
                {identityConfidence.level === 'early' ? 'Track Development' : 'Create Opportunity'}
              </DialogTitle>
              {isPrefilledFromSignal && (
                <DialogDescription className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-[#C9A227] border-[#C9A227]/30 bg-[#C9A227]/5">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Pre-filled from Market Signal
                  </Badge>
                </DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-5 mt-4">
          {/* Company Name with Identity Confidence */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="companyName" className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                Company / Property Name *
              </Label>
              {isPrefilledFromSignal && (
                <div className="flex items-center gap-2">
                  {/* Identity Confidence Indicator */}
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${
                      identityConfidence.color === 'emerald' ? 'text-emerald-600 border-emerald-300 bg-emerald-50' :
                      identityConfidence.color === 'amber' ? 'text-amber-600 border-amber-300 bg-amber-50' :
                      identityConfidence.color === 'red' ? 'text-red-600 border-red-300 bg-red-50' :
                      'text-gray-600 border-gray-300 bg-gray-50'
                    }`}
                  >
                    <span className="mr-1">{identityConfidence.icon}</span>
                    {identityConfidence.label}
                  </Badge>
                </div>
              )}
            </div>
            
            <div className="relative">
              <Input
                id="companyName"
                value={companyName}
                onChange={(e) => handleCompanyNameChange(e.target.value)}
                placeholder="Enter company or property name"
                className={`border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227]/20 pr-24 ${
                  isWorkingTitle ? 'border-amber-300 bg-amber-50/50' : ''
                }`}
                data-testid="opp-company-name"
              />
              {isPrefilledFromSignal && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleEnrichIdentity}
                  disabled={enriching}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                  title="Attempt to find more identity information"
                >
                  {enriching ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <Search className="w-3 h-3 mr-1" />
                  )}
                  Enrich
                </Button>
              )}
            </div>
            
            {/* Working Title Label */}
            {isWorkingTitle && (
              <div className="flex items-center gap-1.5 text-xs text-amber-600">
                <Zap className="w-3 h-3" />
                <span>Working title (no official name identified)</span>
              </div>
            )}
          </div>

          {/* What We Know Section */}
          {isPrefilledFromSignal && signal && (
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Info className="w-4 h-4" />
                What We Know
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                {(city || provinceState || country) && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="w-3 h-3 text-slate-400" />
                    <span>{[city, provinceState, country].filter(Boolean).join(', ') || 'Unknown'}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-slate-600">
                  <Building2 className="w-3 h-3 text-slate-400" />
                  <span>{getPropertyTypeLabel(signal.signal_type)}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Radio className="w-3 h-3 text-slate-400" />
                  <span>{getSignalTypeLabel(signal.signal_type)}</span>
                </div>
                {signal.source_name && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Globe className="w-3 h-3 text-slate-400" />
                    <span>{signal.source_name}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Location */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              Location
            </Label>
            <div className="grid grid-cols-3 gap-3">
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
                className="border-gray-200 focus:border-[#C9A227]"
                data-testid="opp-city"
              />
              <Input
                value={provinceState}
                onChange={(e) => setProvinceState(e.target.value)}
                placeholder="Province/State"
                className="border-gray-200 focus:border-[#C9A227]"
                data-testid="opp-province"
              />
              <Input
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="Country"
                className="border-gray-200 focus:border-[#C9A227]"
                data-testid="opp-country"
              />
            </div>
          </div>

          {/* Opportunity Type & Stage */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Opportunity Type</Label>
              <Select value={opportunityType} onValueChange={setOpportunityType}>
                <SelectTrigger className="border-gray-200" data-testid="opp-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {OPPORTUNITY_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Stage</Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger className="border-gray-200" data-testid="opp-stage">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Estimated Value & Confidence */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Estimated Value ($)</Label>
              <Input
                type="number"
                value={estimatedValue}
                onChange={(e) => setEstimatedValue(e.target.value)}
                placeholder="50000"
                className="border-gray-200 focus:border-[#C9A227]"
                data-testid="opp-value"
              />
            </div>
            {isPrefilledFromSignal && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Confidence Score
                  <Badge variant="outline" className="text-xs">From Signal</Badge>
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={confidenceScore}
                    onChange={(e) => setConfidenceScore(e.target.value)}
                    placeholder="70"
                    min="0"
                    max="100"
                    className="border-gray-200 focus:border-[#C9A227]"
                    data-testid="opp-confidence"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
              </div>
            )}
          </div>

          {/* AI Summary */}
          {isPrefilledFromSignal && aiSummary && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-500" />
                AI Opportunity Summary
                <Badge className="bg-amber-500/10 text-amber-600 border-0 text-xs">AI Generated</Badge>
              </Label>
              <div className="relative">
                <Textarea
                  value={aiSummary}
                  onChange={(e) => setAiSummary(e.target.value)}
                  placeholder="AI-generated summary..."
                  className="min-h-[100px] border-amber-200 bg-amber-50/50 focus:border-amber-400"
                  data-testid="opp-ai-summary"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                This summary explains why this is a strong opportunity. You can edit it before saving.
              </p>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              Notes
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes..."
              className="min-h-[80px] border-gray-200 focus:border-[#C9A227]"
              data-testid="opp-notes"
            />
          </div>

          {/* Source Badge */}
          {isPrefilledFromSignal && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 border border-gray-100">
              <Zap className="w-4 h-4 text-[#C9A227]" />
              <span className="text-sm text-muted-foreground">
                Source: <span className="font-medium text-foreground">Market Signal</span>
                {signal?.signal_type && (
                  <span className="text-muted-foreground"> ({signal.signal_type.replace(/_/g, ' ')})</span>
                )}
              </span>
              {identityConfidence.level === 'early' && (
                <Badge variant="outline" className="ml-auto text-xs text-red-600 border-red-200">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Early Signal
                </Badge>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            
            <div className="flex-1 flex gap-3">
              <Button
                type="submit"
                disabled={loading || !companyName.trim()}
                className="flex-1 bg-gray-800 hover:bg-gray-900"
                data-testid="create-opp-btn"
              >
                {loading && !creatingFully ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Target className="w-4 h-4 mr-2" />
                )}
                {primaryCtaText}
              </Button>
              
              {isPrefilledFromSignal && (
                <Button
                  type="button"
                  onClick={(e) => handleSubmit(e, true)}
                  disabled={loading || !companyName.trim()}
                  className="flex-1 bg-[#C9A227] hover:bg-[#B8922A] text-white"
                  data-testid="create-fully-built-btn"
                >
                  {creatingFully ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  {secondaryCtaText}
                </Button>
              )}
            </div>
          </div>
          
          {isPrefilledFromSignal && (
            <p className="text-xs text-center text-muted-foreground">
              <strong>{secondaryCtaText}</strong> automatically sets stage to Prospecting, attaches signal data, and adds AI summary to notes.
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
